import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  Input,
  Select,
  Row,
  Col,
  InputNumber,
  Button,
  Tooltip,
  Typography,
  Checkbox,
} from 'antd';

import { CloseOutlined } from '@ant-design/icons';
import { sortBy } from 'lodash';

import {
  Form,
  WrapperSelect,
  ThumbnailItem,
  Fieldset,
  FullWidthSpace,
} from 'common/components';
import BrickCodeInput from './BrickCodeInput';
import ProductEditBrandModal from 'pages/product-full-view/components/product-edit-brand/ProductEditBrandModal';
import { ProductCategory } from './ProductCategory';
import { ChargeActiveProduct } from './make-product-active/product-form/ChargeActiveProduct';
import { ResetFormStatus } from './make-product-active/product-form/ResetFormStatus';

import { useValidateStatusField } from 'hooks/useValidateStatusField';

import { useGetMemberInfo } from './hooks';
import { useGetEnumsByEnumName } from 'hooks';
import { PACKAGE_LEVELS, STATUS_PRODUCT } from 'static/Constants';
import { useCheckIsProductOverlay } from 'hooks/product';

import { useIntl } from 'react-intl';
import messages from 'i18n/messages/product';

import { useCheckPermissionEditBrand } from 'hooks/useCheckPermissionEditBrand';
// import CreateSupplier from './CreateSupplier';
// import { defaultType } from 'pages/qa-spec/constant';
import { OverlayInputWrapper } from '../overlay';

import { get, isEqual } from 'lodash';

import { useGetMemberHeaderFromMemberId } from 'common/queries/member-module';

import { ERROR_FIELD_NAME_COLLECTION } from '../preview-requirements/constants';
// import sortByKey from 'utils/sortByKey';

import {
  disableStatusSelectionInEditMode,
  enableMakeActiveProduct,
} from 'utils/ixone-capture';

import './CreateProductForm.less';

import { useUserInfo } from 'hooks/auth/useUserInfo';

const { Option } = Select;
const { Text } = Typography;

const CreateProductForm = (props) => {
  const intl = useIntl();

  const {
    form,
    productData,
    formMode,
    targetMarketList,
    productEnums,
    currentHierarchy,
    brick,
    updateBrickCodeData,
    handleResetBrandId,
    setBrandId,
    editBrandVisible,
    setEditBrandVisible,
    brandThumb,
    setBrandThumb,
    errorInfo,
    memberIdBrand,
    setProductCategory,
    originProductFull,
    productFull,
  } = props;

  const isProductOverlay = useCheckIsProductOverlay({
    productFull: formMode === 'edit' ? productFull : null,
  });

  const { userInfo } = useUserInfo();

  const { memberInfo: companyInfo } = useGetMemberInfo(memberIdBrand);
  const { data: memberHeaderOfProductOwner } = useGetMemberHeaderFromMemberId({
    memberId: productData?.ownerId,
    enabled: Boolean(productData && productData?.ownerId),
  });
  const isVariableWeightsAndMeasures = Form.useWatch(
    'isVariableWeightsAndMeasures',
    form
  );

  const isEdit = formMode === 'edit';

  const isActiveStatus = productData?.status?.toLowerCase() === 'active';

  /* match conditions for making active product and status is not active  */
  const isDisplayActiveProductBtn =
    enableMakeActiveProduct(productData) && !isActiveStatus && isEdit;

  const { data: memberHeaderOfProductCreator } = useGetMemberHeaderFromMemberId(
    {
      memberId: memberIdBrand,
      enabled: !!memberIdBrand,
    }
  );

  const hasPermissionEditBrand = useCheckPermissionEditBrand(
    memberIdBrand,
    userInfo
  );

  const { statusValidateProps } = useValidateStatusField({
    form,
    entityType: 'Product',
    entityId: productData?.productId,
    placement: 'rightBottom',
  });

  const qaTypeEnums = useGetEnumsByEnumName('ProductOverviewTypeCodeEnum');

  const handleNumberInput = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const initFieldsError = () => {
    if (!errorInfo) return;

    form.setFields(errorInfo);
  };

  const sortTargetMarket = () => {
    if (!targetMarketList) return [];
    const USMarket = targetMarketList.find((marketItem, index) => {
      if (marketItem.Numeric !== 840) return false;

      return true;
    });

    const noneUSMarketList = targetMarketList.filter((marketItem) => {
      return marketItem.Numeric !== 840;
    });

    const sortedNoneUSMarketList = noneUSMarketList.sort((a, b) => {
      return a.Country - b.Country;
    });

    return [USMarket, ...sortedNoneUSMarketList];
  };

  const handleChangeVariableWeightsAndMeasures = (e) => {
    const formValues = form.getFieldsValue();
    const formValuesResult = {
      ...formValues,
      weight: productData?.weight,
      averageWeight: productData?.averageWeight,
    };
    form.setFieldsValue(formValuesResult);
  };

  const productBasicUOM = productEnums?.filter(
    (type) => type.enumName === 'BasicUomEnum'
  );

  const packageLevelWarning = currentHierarchy?.productHierarchyId
    ? {
        help: intl.formatMessage(messages.packageLevelTooltip),
        validateStatus: 'warning',
        hasFeedback: true,
      }
    : {};

  const enumProperties =
    productBasicUOM?.[0]?.enumProperties?.filter((type) =>
      ['Mass', 'Count', 'Volume', 'Dimensions', 'Area'].includes(type.enumGroup)
    ) || [];

  const enumMassProperties =
    productBasicUOM?.[0]?.enumProperties?.filter(
      (type) => type.enumGroup === 'Mass'
    ) || [];

  const enumDimensionsProperties =
    productBasicUOM?.[0]?.enumProperties?.filter(
      (type) => type.enumGroup === 'Dimensions'
    ) || [];

  const selectionFilter = useCallback(
    (input, option) =>
      option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0,
    []
  );

  const isPrivateVisibilityProduct = () => {
    if (formMode === 'create') {
      if (companyInfo || memberIdBrand) {
        return (
          memberHeaderOfProductCreator?.products?.toLowerCase() === 'private'
        );
      }
    }

    if (formMode === 'edit') {
      return memberHeaderOfProductOwner?.products?.toLowerCase() === 'private';
    }

    //* default return;
    return memberHeaderOfProductCreator?.products?.toLowerCase() === 'private';
  };

  const sortedTargetMarket = sortTargetMarket();

  //* add product
  const showRetailerProductCreation = (() => {
    if (isEdit) return false;

    /**
     ** check permission - create product for current user
     */
    if (!memberIdBrand && userInfo?.member?.memberType) {
      if (userInfo?.member?.memberType?.toLowerCase() === 'retailer')
        return true;
    }

    /**
     ** check permission - create product for member maintenance view
     */
    if (companyInfo || memberIdBrand) {
      if (companyInfo?.memberType?.toLowerCase() === 'retailer') return true;
      else return false;
    }

    return false;
  })();

  //* permssion edit private label
  const showEditPrivateLabel = (() => {
    const privateLabelRoles = ['retailer', 'distributor'];

    /**
     ** check permission - create product for current user
     */
    if (!isEdit) {
      if (!memberIdBrand && userInfo?.member?.memberType) {
        if (
          privateLabelRoles.includes(
            userInfo?.member?.memberType?.toLowerCase()
          )
        ) {
          return true;
        }
      }
    }

    /**
     ** check permission - create product for member in maintain member view
     */
    if (!isEdit) {
      if (companyInfo || memberIdBrand) {
        if (
          privateLabelRoles.includes(companyInfo?.memberType?.toLowerCase())
        ) {
          return true;
        }
      }
    }

    /**
     ** check permission - edit product
     */
    if (isEdit) {
      if (privateLabelRoles.includes(productData?.ownerRole?.toLowerCase())) {
        return true;
      }
    }

    return false;
  })();

  useEffect(() => {
    initFieldsError();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        packageLevel: PACKAGE_LEVELS[0],
        isPrivateLabel: true,
      });
    }
  }, [isEdit]);

  const brandError = errorInfo?.find((errorItem) =>
    errorItem.name.includes(ERROR_FIELD_NAME_COLLECTION.BRAND)
  );

  const upcGtinError = errorInfo?.find((errorItem) =>
    errorItem.name.includes(ERROR_FIELD_NAME_COLLECTION.AT_LEAST_GTIN_UPC)
  );

  const brandErrorMessage = brandError ? brandError.errors[0] : '';
  const upcGtinErrorMessage = upcGtinError ? upcGtinError.errors[0] : '';
  const getOverlayProps = (path) => {
    const originValue = get(originProductFull, path);

    return {
      checkShowOverlayFn: ({ value }) => {
        return (
          !isEqual(value ?? null, originValue ?? null) &&
          isProductOverlay &&
          !!originProductFull
        );
      },
      originValue: originValue || 'N/A',
    };
  };
  const getBrickCodeOverlayProps = () => {
    const brickCodeFormatted = brick?.brickCode?.toString() ?? '';
    const originalBrickCode = originProductFull?.brickCode?.toString() ?? '';
    const isBrickCodeDifferent = brickCodeFormatted !== originalBrickCode;

    const originValue = originProductFull?.brickCode || 'N/A';
    return {
      showOverlay: isProductOverlay ? isBrickCodeDifferent : null,
      originValue: isProductOverlay ? originValue : null,
    };
  };

  const getBrickCodeNameOverlayProps = () => {
    const brickCodeNameFormatted = brick?.brickCodeName?.toString() ?? '';
    const originalBrickCodeName =
      originProductFull?.brickCodeName?.toString() ?? '';
    const isBrickCodeNameDifferent =
      brickCodeNameFormatted !== originalBrickCodeName;

    const originValue = originProductFull?.brickCodeName || 'N/A';
    return {
      showOverlay: isProductOverlay ? isBrickCodeNameDifferent : null,
      originValue: isProductOverlay ? originValue : null,
    };
  };

  const renderProductFieldset = () => {
    return (
      <Fieldset title='Product' id='product-field'>
        <Form.Item
          label='Product Name'
          name='productName'
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.productNameRequired),
            },
          ]}
        >
          <OverlayInputWrapper {...getOverlayProps('productName')}>
            <Input />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='Primary Size' name='primarySize'>
          <OverlayInputWrapper {...getOverlayProps('primarySize')}>
            <InputNumber
              style={styles.numericInput}
              min={0}
              max={999999}
              precision={3}
            />
          </OverlayInputWrapper>
        </Form.Item>

        {/*//! LACK INFORMATION, NEED TO UPDATE LATER */}
        <Form.Item label='Primary Size UOM' name='primarySizeUOM'>
          <OverlayInputWrapper {...getOverlayProps('primarySizeUOM')}>
            <WrapperSelect
              showSearch
              filterOption={selectionFilter}
              getPopupContainer={() => document.getElementById('product-field')}
            >
              {[
                sortBy([...enumProperties], ['enumDisplayName']).map(
                  (type, index) => {
                    return (
                      <Option value={type?.enumDisplayName} key={index}>
                        {type?.enumDisplayName}
                      </Option>
                    );
                  }
                ),
              ]}
            </WrapperSelect>
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='Description' name='productDescription'>
          <OverlayInputWrapper {...getOverlayProps('productDescription')}>
            <Input.TextArea rows={7} />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='Unit Count' name='unitCount'>
          <OverlayInputWrapper {...getOverlayProps('unitCount')}>
            <InputNumber type='number' style={styles.numericInput} />
          </OverlayInputWrapper>
        </Form.Item>
      </Fieldset>
    );
  };

  const renderClassificationFieldset = () => {
    return (
      <Fieldset title='Classification' id='classification-field'>
        {/* {showRetailerProductCreation && (
          <Form.Item label='Type' name='type'>
            <WrapperSelect
              showSearch
              filterOption={selectionFilter}
              allowClear={false}
              defaultValue={defaultType}
              getPopupContainer={(toggleEl) =>
                document.getElementById('classification-field')
              }
              disabled={isProductOverlay}
            >
              {sortByKey(qaTypeEnums, 'enumDisplayName')?.map((item, index) => (
                <Select.Option
                  key={index}
                  value={item.enumDisplayName ?? defaultType}
                >
                  {item.enumDisplayName ?? defaultType}
                </Select.Option>
              ))}
            </WrapperSelect>
          </Form.Item>
        )} */}

        <Form.Item
          name='packageLevel'
          label='Package Level'
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.productPackageLvRequired),
            },
          ]}
          {...packageLevelWarning}
        >
          <OverlayInputWrapper {...getOverlayProps('packageLevel')}>
            <WrapperSelect
              placeholder='Select a package level'
              disabled={
                currentHierarchy?.productHierarchyId || isProductOverlay
              }
              allowClear
              listHeight={160}
              getPopupContainer={() =>
                document.getElementById('classification-field')
              }
            >
              {PACKAGE_LEVELS?.map((packageLevel, index) => (
                <Option value={packageLevel} key={index}>
                  {packageLevel}
                </Option>
              ))}
            </WrapperSelect>
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item name='brick' label='Brick Code'>
          <OverlayInputWrapper
            style={{ paddingRight: '10px' }}
            {...getBrickCodeOverlayProps()}
          >
            <BrickCodeInput
              updateBrickCodeData={updateBrickCodeData}
              initialValue={brick}
              disabled={isProductOverlay}
            />
          </OverlayInputWrapper>
        </Form.Item>
        <Form.Item label='Brick Code Name'>
          <OverlayInputWrapper {...getBrickCodeNameOverlayProps()}>
            <Input value={brick?.brickCodeName} disabled />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item
          label={intl.formatMessage(messages.targetMarket)}
          name='targetMarket'
        >
          <OverlayInputWrapper {...getOverlayProps('targetMarket')}>
            <WrapperSelect
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              getPopupContainer={() =>
                document.getElementById('classification-field')
              }
              disabled={isProductOverlay}
            >
              {targetMarketList?.length > 0 &&
                sortedTargetMarket?.map((item, index) => (
                  <Select.Option key={index} value={item?.Country}>
                    {item?.Country}
                  </Select.Option>
                ))}
            </WrapperSelect>
          </OverlayInputWrapper>
        </Form.Item>
        {/* {showRetailerProductCreation && (
          <Form.Item label='Retail Banner' name='retailBanner'>
            <Input />
          </Form.Item>
        )} */}

        {/* {showRetailerProductCreation && (
          <Form.Item
            label='USDA WIC Eligible'
            name='wicCompliant'
            valuePropName='checked'
          >
            <Checkbox />
          </Form.Item>
        )} */}
      </Fieldset>
    );
  };

  const renderGtinFieldset = () => {
    return (
      <Fieldset title='GTIN' id='gtin-field'>
        <Form.Item label='GTIN' name='gtin'>
          <OverlayInputWrapper {...getOverlayProps('gtin')}>
            <Input
              min='0'
              onKeyPress={handleNumberInput}
              maxLength={MAX_NUMBER_INPUT_UPC}
              disabled={isProductOverlay}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='UPC8' name='upc8'>
          <OverlayInputWrapper {...getOverlayProps('upc8')}>
            <Input
              min='0'
              onKeyPress={handleNumberInput}
              maxLength={MAX_NUMBER_INPUT_UPC}
              disabled={isProductOverlay}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='UPC10' name='upc10'>
          <OverlayInputWrapper {...getOverlayProps('upc10')}>
            <Input
              min='0'
              onKeyPress={handleNumberInput}
              maxLength={MAX_NUMBER_INPUT_UPC}
              disabled={isProductOverlay}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='UPC11' name='upc11'>
          <OverlayInputWrapper {...getOverlayProps('upc11')}>
            <Input
              min='0'
              onKeyPress={handleNumberInput}
              maxLength={MAX_NUMBER_INPUT_UPC}
              disabled={isProductOverlay}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='UPC12' name='upc12'>
          <OverlayInputWrapper {...getOverlayProps('upc12')}>
            <Input
              min='0'
              onKeyPress={handleNumberInput}
              maxLength={MAX_NUMBER_INPUT_UPC}
              disabled={isProductOverlay}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='UPC13' name='upc13'>
          <OverlayInputWrapper {...getOverlayProps('upc13')}>
            <Input
              min='0'
              onKeyPress={handleNumberInput}
              maxLength={MAX_NUMBER_INPUT_UPC}
              disabled={isProductOverlay}
            />
          </OverlayInputWrapper>
        </Form.Item>
        {upcGtinError ? <Text type='danger'>{upcGtinErrorMessage}</Text> : null}
      </Fieldset>
    );
  };

  const renderBrandFieldset = () => {
    return hasPermissionEditBrand ? (
      <Fieldset title='Brand / Category'>
        <Row gutter={16}>
          <Col xxl={10} xl={12}>
            <Form.Item name='brandId'>
              <Form.Item>
                <div className='add-product__thumb-wrapper'>
                  <div>
                    <CloseOutlined
                      className='add-product__thumb-wrapper-close-btn'
                      onClick={handleResetBrandId}
                    />
                  </div>
                  <ThumbnailItem
                    hideLabel={true}
                    imageThumbnail={brandThumb}
                    hideCheckbox
                    style={{ marginLeft: 0 }}
                  />
                </div>
                <Tooltip title='Choose an Brand ID'>
                  <Button
                    onClick={() => setEditBrandVisible(true)}
                    disabled={isProductOverlay}
                  >
                    Link to Brand
                  </Button>
                </Tooltip>
              </Form.Item>
            </Form.Item>
          </Col>
          <Col xxl={14} xl={12}>
            <ProductCategory
              setProductCategory={setProductCategory}
              getOverlayProps={getOverlayProps}
              isProductOverlay={isProductOverlay}
            />
          </Col>
        </Row>
        {brandError ? <Text type='danger'>{brandErrorMessage}</Text> : null}
        {editBrandVisible && (
          <ProductEditBrandModal
            visible={editBrandVisible}
            setVisibleEditBrand={setEditBrandVisible}
            productDetail={productData}
            isAddProduct={true}
            isEdit={isEdit}
            setBrandId={setBrandId}
            setBrandThumb={setBrandThumb}
            memberIdFromBrand={memberIdBrand}
          />
        )}
      </Fieldset>
    ) : null;
  };

  const renderPackageWeightFieldset = () => {
    return (
      <Fieldset title='Packaging Weights and Measures' id='package-field'>
        <Form.Item label='Packaged Width' name='width'>
          <OverlayInputWrapper {...getOverlayProps('width')}>
            <InputNumber
              style={styles.numericInput}
              min={0}
              max={MAX_NUMBER_INPUT_UPC}
              precision={3}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='Packaged Height' name='height'>
          <OverlayInputWrapper {...getOverlayProps('height')}>
            <InputNumber
              style={styles.numericInput}
              min={0}
              max={MAX_NUMBER_INPUT_UPC}
              precision={3}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='Packaged Depth' name='depth'>
          <OverlayInputWrapper {...getOverlayProps('depth')}>
            <InputNumber
              style={styles.numericInput}
              min={0}
              max={MAX_NUMBER_INPUT_UPC}
              precision={3}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='Packaged Weight' name='weight'>
          <OverlayInputWrapper {...getOverlayProps('weight')}>
            <InputNumber
              style={styles.numericInput}
              min={0}
              max={MAX_NUMBER_INPUT_UPC}
              precision={3}
              disabled={isVariableWeightsAndMeasures}
            />
          </OverlayInputWrapper>
        </Form.Item>
        <Form.Item label='Average Weight' name='averageWeight'>
          <OverlayInputWrapper {...getOverlayProps('averageWeight')}>
            <InputNumber
              style={styles.numericInput}
              min={0}
              max={MAX_NUMBER_INPUT_UPC}
              disabled={!isVariableWeightsAndMeasures}
            />
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='Packaged Weight UOM' name='weightUOM'>
          <OverlayInputWrapper {...getOverlayProps('weightUOM')}>
            <WrapperSelect
              showSearch
              filterOption={selectionFilter}
              getPopupContainer={() => document.getElementById('package-field')}
            >
              {[
                sortBy([...enumMassProperties], ['enumDisplayName']).map(
                  (type, index) => {
                    return (
                      <Option value={type?.enumDisplayName} key={index}>
                        {type?.enumDisplayName}
                      </Option>
                    );
                  }
                ),
              ]}
            </WrapperSelect>
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item label='Packaged Dimension UOM' name='dimensionUOM'>
          <OverlayInputWrapper {...getOverlayProps('dimensionUOM')}>
            <WrapperSelect
              showSearch
              filterOption={selectionFilter}
              getPopupContainer={() => document.getElementById('package-field')}
            >
              {[
                sortBy([...enumDimensionsProperties], ['enumDisplayName']).map(
                  (type, index) => {
                    return (
                      <Option value={type?.enumDisplayName} key={index}>
                        {type?.enumDisplayName}
                      </Option>
                    );
                  }
                ),
              ]}
            </WrapperSelect>
          </OverlayInputWrapper>
        </Form.Item>
      </Fieldset>
    );
  };

  const renderSecurityFieldset = () => {
    return (
      <Fieldset title='Security' id='security-field'>
        <Form.Item name='visibility' label='Visibility'>
          <OverlayInputWrapper {...getOverlayProps('visibility')}>
            {isPrivateVisibilityProduct() ? (
              <WrapperSelect
                disabled={productData?.gdsnShield}
                getPopupContainer={() =>
                  document.getElementById('security-field')
                }
              >
                <Option value='Only If Shared'>Only If Shared</Option>
                <Option value='Private'>Private</Option>
                {/* <Option value='None'>None</Option> */}
              </WrapperSelect>
            ) : (
              <WrapperSelect
                disabled={productData?.gdsnShield}
                getPopupContainer={() =>
                  document.getElementById('security-field')
                }
              >
                <Option value='Only If Shared'>Only If Shared</Option>
                <Option value='Public'>Public</Option>
                <Option value='Private'>Private</Option>
                {/* <Option value='None'>None</Option> */}
              </WrapperSelect>
            )}
          </OverlayInputWrapper>
        </Form.Item>

        <Form.Item
          name='status'
          label='Status'
          {...statusValidateProps}
          style={{ marginBottom: 6 }}
        >
          <OverlayInputWrapper {...getOverlayProps('status')}>
            <WrapperSelect
              getPopupContainer={() =>
                document.getElementById('security-field')
              }
              suffixIcon={
                isEdit ? (
                  <ResetFormStatus form={form} productData={productData} />
                ) : null
              }
              allowClear={isEdit ? false : true}
              disabled={productData?.status?.toLowerCase() === 'exception'}
            >
              {[
                sortBy([...STATUS_PRODUCT], ['displayName']).map(
                  (status, index) => {
                    return (
                      <Option
                        value={status?.value}
                        key={index}
                        disabled={disableStatusSelectionInEditMode({
                          valueOption: status.value,
                          ...productData,
                        })}
                      >
                        {status?.displayName}
                      </Option>
                    );
                  }
                ),
              ]}
            </WrapperSelect>
          </OverlayInputWrapper>
        </Form.Item>

        {isDisplayActiveProductBtn && (
          <ChargeActiveProduct
            entityId={productData?.productId}
            entityType='Product'
          />
        )}
      </Fieldset>
    );
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      name='Add-Product'
      preserve={false}
      className='add-product__modal'
    >
      <Row gutter={16} style={{ overflowX: 'hidden' }}>
        {/* {isEdit && (
          <Col span={8}>
            <Form.Item label='RIVIR ID'>
              <Input value={productData?.rivirId} disabled />
            </Form.Item>
          </Col>
        )}
        {showRetailerProductCreation && (
          <Col span={8}>
            <CreateSupplier isCreateProductModal />
          </Col>
        )} */}

        <Col span={18}>
          {showEditPrivateLabel && (
            <Form.Item
              label='Private Label'
              name='isPrivateLabel'
              valuePropName='checked'
              colon={false}
              labelAlign='right'
              labelCol={{
                flex: '85px',
              }}
              wrapperCol={{
                flex: 1,
              }}
            >
              <Checkbox
                onChange={(e) => {
                  if (!e.target.checked)
                    form?.setFieldsValue({ supplierId: null });
                }}
              />
            </Form.Item>
          )}
        </Col>

        <Col
          span={6}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          <Form.Item
            label='Variable Weights and Measures'
            name='isVariableWeightsAndMeasures'
            valuePropName='checked'
            colon={false}
            labelCol={{
              flex: 'auto',
            }}
            wrapperCol={{
              flex: 'auto',
            }}
          >
            <Checkbox
              onChange={handleChangeVariableWeightsAndMeasures}
              disabled={isProductOverlay}
            />
          </Form.Item>
        </Col>
      </Row>

      <FullWidthSpace size={16}>
        <Row gutter={16}>
          <Col span={8}>{renderProductFieldset()}</Col>
          <Col span={8}>{renderGtinFieldset()}</Col>
          <Col span={8}>{renderPackageWeightFieldset()}</Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>{!isProductOverlay && renderSecurityFieldset()}</Col>
          <Col span={8}>{renderBrandFieldset()}</Col>
          <Col span={8}>{renderClassificationFieldset()}</Col>
        </Row>
      </FullWidthSpace>
    </Form>
  );
};

const styles = {
  numericInput: {
    width: '100%',
  },
};

const MAX_NUMBER_INPUT_UPC = 9999999999999999999;

const formItemLayout = {
  labelCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 8,
    },
  },
  wrapperCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 15,
    },
  },
};

export default CreateProductForm;
